/* eslint-disable no-param-reassign */
import {
  createAction,
  createReducer,
  createSelector,
  createAsyncThunk,
} from '@reduxjs/toolkit'
import yn from 'yn'
import { redFunnelAPI } from '../api/api'

const handleBeginRequest = createAction('auth/beginRequest')
export const handleReceiveUser = createAction('auth/receive')
export const handleError = createAction('auth/error')

export const logout = createAsyncThunk('auth/logout', (args, thunkAPI) => {
  const user = selectUser(thunkAPI?.getState())
  try {
    sessionStorage.removeItem('rfUseTravelCard')
  } catch (err) {
    // ignore storage errors
  }
  return redFunnelAPI.logout(user)
})

export const handleTravelCard = createAction('auth/travelCard', value => ({
  payload: !!value,
}))

const initialState = {
  loading: false,
  user: null,
  travelCard: undefined,
  error: null,
}

export const authReducer = createReducer(initialState, builder => {
  builder
    .addCase(handleBeginRequest, state => {
      state.loading = true
      state.error = null
    })
    .addCase(handleReceiveUser, (state, action) => {
      state.loading = false
      state.user = action.payload
      state.error = null
    })
    .addCase(handleTravelCard, (state, action) => {
      // travel card belongs to a logged in account. A dispatch arriving after
      // logout (stale closure, in-flight fetch) must not resurrect the flag -
      // that is what was writing rfUseTravelCard back as false.
      if (!state.user) return
      state.travelCard = action.payload
    })
    .addCase(handleError, (state, action) => {
      state.loading = false
      state.error = !!action.payload
    })
    .addCase(logout.pending, state => {
      state.loading = true
    })
    .addCase(logout.fulfilled, state => {
      state.loading = false
      state.user = null
      state.travelCard = undefined
    })
    .addCase(logout.rejected, state => {
      state.loading = false
      state.user = null
      state.travelCard = undefined
    })
})

export const selectAuthState = state => state?.userData

export const reducerCustomer = user => ({
  ...(user || {}),
  id: user.id,
  fullName: user?.customerAccount?.firstAndLastName || '',
  firstName: user?.customerAccount?.firstName || '',
  lastName: user?.customerAccount?.lastName || '',
  email: user?.customerAccount?.email || '',
  customerNumber: user?.customerAccount?.customerNumber || '',
  mobilePhoneNumber: user?.customerAccount?.mobilePhoneNumber || '',
  initials: `${user?.customerAccount?.firstName[0] || ''}${
    user?.customerAccount?.lastName[0] || ''
  }`.trim(),
  address: user?.customerAccount?.address || '',
  city: user?.customerAccount?.city || '',
  postCode: user?.customerAccount?.postCode || '',
  countryCode: user?.customerAccount?.countryCode || '',
  countryName: user?.customerAccount?.countryName || '',
  role: 'customer',
  accountLink: '/my-account',
  defaultProductCode: user?.customerAccount?.defaultProductCode,
})

export const reduceTradeAgent = user => ({
  ...(user || {}),
  id: user?.agentUser?.userName,
  fullName: user?.agentUser?.name,
  firstName: user?.agentUser?.name || '',
  email: user?.agentUser?.email,
  initials: user?.agentUser?.initials,
  customerNumber: user?.agentUser?.userName,
  agentNumber: user?.agentUser?.agentNumber,
  role: 'agentUser',
  accountLink: '/agent-portal',
  accountType: user?.agentUser?.accountType,
  paymentType: user?.agentUser?.paymentType,
  paymentMethod: user?.agentUser?.paymentMethod,
  poNumberRequired: yn(user?.agentUser?.poNumberRequired),
  defaultPoNumber: user?.agentUser?.defaultPoNumber,
})

const reduceAccountAgent = user => {
  return {
    ...user,
    fullName: user.name,
    firstName: user.name,
    initials:
      user.name
        ?.split(' ')
        ?.map(name => name[0])
        ?.join('') || '',
    customerNumber: user?.agentNumber,
    agentNumber: user?.agentNumber,
    role: 'accountAgent',
    accountLink: '/agent-portal',
    poNumberRequired: yn(user?.poNumberRequired),
    defaultPoNumber: user?.defaultPoNumber,
  }
}

export const selectUser = createSelector(selectAuthState, state => {
  const user = state?.user

  if (user) {
    if (user?.customerAccount !== undefined) {
      return reducerCustomer(user)
    }

    if (user?.agentUser !== undefined) {
      return reduceTradeAgent(user)
    }

    if (user.agentNumber !== undefined) {
      return reduceAccountAgent(user)
    }

    if (user.aspNetSessionId !== undefined) {
      return {
        role: 'callCentre',
        initials: 'CC',
        accountLink: '/contact-centre',
        accountLinkText: 'Bookings Dashboard',
      }
    }
  }
  return user
})
export const selectAuthIsLoading = createSelector(
  selectAuthState,
  state => state?.loading,
)
export const selectAuthError = createSelector(
  selectAuthState,
  state => state?.error,
)

export const selectUserRole = createSelector(
  selectUser,
  user => user?.role || 'customer',
)

export const selectTravelCard = createSelector(
  selectAuthState,
  state => state?.travelCard,
)

export const selectUserCategory = createSelector(
  selectUser,
  user => user?.category || '',
)

export const selectIsFreightAgent = createSelector(
  selectUserCategory,
  category => category === 'F',
)

export const selectIsAgent = createSelector(
  selectUserRole,
  role => role === 'agentUser' || role === 'accountAgent',
)

export const selectAgentId = createSelector(
  selectUser,
  user => user?.agentNumber || null,
)

export const selectIsCreditAccount = createSelector(
  selectUser,
  user =>
    user?.agentUser?.accountType === 'INVOICE' ||
    user?.accountType === 'INVOICE',
)

export const selectIsCashAccount = createSelector(
  selectUser,
  user =>
    user?.agentUser?.accountType === 'CASH' || user?.accountType === 'CASH',
)

export const selectIsContactCentre = createSelector(
  selectUserRole,
  role => role === 'callCentre',
)
